
import { useState, useEffect } from "react";
import { getUserCourses } from "@/lib/user-courses";
import { toast } from "@/components/ui/use-toast";
import { Lesson } from "@/types/supabase";

/**
 * Hook to check user enrollment status for a course
 */
export function useEnrollmentStatus(
  userId: string | undefined, 
  courseId: string | undefined,
  lessons: Lesson[]
) {
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [userCourse, setUserCourse] = useState<any>(null);
  const [isCheckingEnrollment, setIsCheckingEnrollment] = useState(false);
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null);

  useEffect(() => {
    const checkEnrollment = async () => {
      if (!userId || !courseId) return;
      
      try {
        setIsCheckingEnrollment(true);
        setEnrollmentError(null);
        console.log("Checking enrollment for user:", userId, "in course:", courseId);
        
        const userCourses = await getUserCourses(userId);
        const enrollment = userCourses.find(course => course.course_id === courseId);
        
        if (enrollment) {
          setIsEnrolled(true);
          setUserCourse(enrollment);
          console.log("User is enrolled in this course:", enrollment);
        } else {
          setIsEnrolled(false);
          setUserCourse(null);
          console.log("User is not enrolled in this course");
        }
      } catch (error) {
        console.error("Error checking enrollment:", error);
        setEnrollmentError("Failed to check course enrollment status");
        toast({
          title: "Error",
          description: "Failed to check course enrollment status",
          variant: "destructive"
        });
      } finally {
        setIsCheckingEnrollment(false);
      }
    };
    
    // Only check enrollment once both userId and courseId are available
    if (userId && courseId) {
      checkEnrollment();
    }
  }, [userId, courseId, lessons]);

  return {
    isEnrolled,
    userCourse,
    isCheckingEnrollment,
    enrollmentError
  };
}
